import React, { useState, useEffect, useCallback } from 'react';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';
import {
    getProfitLossSummary,
    getProfitLossFilterOptions,
    getProfitByProducts,
    getProfitByCategories,
    getProfitByBrands,
    getProfitByLocations,
    getProfitByInvoice,
    getProfitByDate,
    getProfitByCustomer,
    getProfitByDay,
    getStockBySellPrice,
} from '../../services/reportApi';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';

const ProfitLossReport = () => {
    const [activeSubTab, setActiveSubTab] = useState('products');
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [summary, setSummary] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [totalProfit, setTotalProfit] = useState({ formatted: '$ 0.00', raw: 0 });
    const [stockData, setStockData] = useState(null);
    const [filterOptions, setFilterOptions] = useState({ locations: {} });
    const [filters, setFilters] = useState({ start_date: '', end_date: '', location_id: '' });

    const subTabs = [
        { id: 'products',   label: 'Profit by products',   icon: 'fa-cube',       api: getProfitByProducts },
        { id: 'categories', label: 'Profit by categories', icon: 'fa-tags',       api: getProfitByCategories },
        { id: 'brands',     label: 'Profit by brands',     icon: 'fa-th-large',   api: getProfitByBrands },
        { id: 'locations',  label: 'Profit by locations',  icon: 'fa-map-marker', api: getProfitByLocations },
        { id: 'invoice',    label: 'Profit by invoice',    icon: 'fa-file-text-o',api: getProfitByInvoice },
        { id: 'date',       label: 'Profit by date',       icon: 'fa-calendar',   api: getProfitByDate },
        { id: 'customer',   label: 'Profit by customer',   icon: 'fa-user',       api: getProfitByCustomer },
        { id: 'day',        label: 'Profit by day',        icon: 'fa-calendar-o', api: getProfitByDay },
        { id: 'stock-by-sell-price', label: 'Stock by sell price', icon: 'fa-money', api: getStockBySellPrice },
    ];

    const datePresets = [
        { label: 'Today', fn: () => { const d = new Date(); const s = d.toISOString().split('T')[0]; return { start: s, end: s }; } },
        { label: 'Yesterday', fn: () => { const d = new Date(); d.setDate(d.getDate()-1); const s = d.toISOString().split('T')[0]; return { start: s, end: s }; } },
        { label: 'Last 7 Days', fn: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate()-6); return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] }; } },
        { label: 'This Month', fn: () => { const d = new Date(); const s = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; const e = d.toISOString().split('T')[0]; return { start: s, end: e }; } },
        { label: 'Last Month', fn: () => { const d = new Date(); d.setMonth(d.getMonth()-1); const s = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; const e = new Date(d.getFullYear(), d.getMonth()+1, 0).toISOString().split('T')[0]; return { start: s, end: e }; } },
        { label: 'This Year', fn: () => { const d = new Date(); const s = `${d.getFullYear()}-01-01`; const e = d.toISOString().split('T')[0]; return { start: s, end: e }; } },
        { label: 'Last Year', fn: () => { const y = new Date().getFullYear()-1; return { start: `${y}-01-01`, end: `${y}-12-31` }; } },
        { label: 'Custom Range', fn: () => ({ start: filters.start_date, end: filters.end_date }) },
    ];

    const fetchSummary = useCallback(async () => {
        try {
            const res = await getProfitLossSummary(filters);
            if (res.success) setSummary(res.data);
        } catch (e) { /* silently fail */ }
    }, [filters]);

    const fetchSubTab = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const tab = subTabs.find(t => t.id === activeSubTab);
            if (!tab) return;
            const res = await tab.api(filters);
            if (res.success) {
                if (activeSubTab === 'stock-by-sell-price') {
                    setStockData(res.data);
                    setTableData([]);
                    setTotalProfit({ formatted: '$ 0.00', raw: 0 });
                } else {
                    setTableData(res.data.data || []);
                    setTotalProfit(res.data.total_profit || { formatted: '$ 0.00', raw: 0 });
                    setStockData(null);
                }
            }
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }, [activeSubTab, filters]);

    useEffect(() => {
        fetchSummary();
        fetchSubTab();
        getProfitLossFilterOptions().then(r => { if (r.success) setFilterOptions(r.data); }).catch(() => {});
    }, []);

    useEffect(() => { fetchSummary(); fetchSubTab(); }, [filters]);
    useEffect(() => { fetchSubTab(); }, [activeSubTab]);

    const applyDatePreset = (preset) => {
        const { start, end } = preset.fn();
        setFilters(f => ({ ...f, start_date: start, end_date: end }));
        setShowDateFilter(false);
    };

    const handleExport = (format) => {
        const headers = [firstColLabel, 'Gross Profit'];
        const rows = filteredData.map(row => {
            const name = row.product || row.category || row.brand || row.location || row.invoice_no || row.transaction_date_formatted || row.customer || row.day || '';
            return [name, row.gross_profit?.formatted || ''];
        });
        const title = `Profit Loss Report - ${activeSubTab}`;
        const filename = `profit_loss_${activeSubTab}`;
        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();
    };

    const exportItems = [
        { icon: 'fa-file-excel-o', label: 'Export CSV', action: () => handleExport('CSV') },
        { icon: 'fa-file-excel-o', label: 'Export Excel', action: () => handleExport('Excel') },
        { icon: 'fa-print',        label: 'Print', action: () => handleExport('Print') },
        { icon: 'fa-file-pdf-o',   label: 'Export PDF', action: () => handleExport('PDF') },
    ];

    const filteredData = tableData.filter(row => {
        const term = searchTerm.toLowerCase();
        const searchable = (row.product || row.category || row.brand || row.location || row.invoice_no || row.transaction_date_formatted || row.transaction_date || row.customer || row.day || '').toLowerCase();
        return searchable.includes(term);
    });

    const paginatedData = filteredData.slice(0, entriesPerPage);

    const currentTab = subTabs.find(t => t.id === activeSubTab);
    const firstColLabel = currentTab ? currentTab.label.replace('Profit by ', '') : 'Product';

    const inputStyle = { padding: '0.4rem 0.6rem', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none' };

    const leftRows = [
        { label: 'Opening Stock (By purchase price):', value: summary?.opening_stock?.by_purchase_price || '$ 0.00' },
        { label: 'Total purchase: (Exc. tax, Discount)', value: summary?.total_purchase?.exc_tax_discount || '$ 0.00', hi: true },
        { label: 'Total Purchase discount:', value: summary?.total_purchase_discount?.formatted || '$ 0.00' },
        { label: 'Total Purchase Return:', value: summary?.total_purchase_return?.formatted || '$ 0.00', hi: true },
        { label: 'Total Stock Adjustment:', value: summary?.total_adjustment?.formatted || '$ 0.00' },
        { label: 'Total Expense:', value: summary?.total_expense?.formatted || '$ 0.00', hi: true },
        { label: 'Total purchase shipping charge:', value: summary?.total_purchase_shipping_charge?.formatted || '$ 0.00' },
        { label: 'Purchase additional expenses:', value: summary?.total_purchase_additional_expense?.formatted || '$ 0.00', hi: true },
        { label: 'Total transfer shipping charge:', value: summary?.total_transfer_shipping_charges?.formatted || '$ 0.00' },
        { label: 'Total Sell discount:', value: summary?.total_sell_discount?.formatted || '$ 0.00', hi: true },
        { label: 'Total customer reward:', value: summary?.total_reward_amount?.formatted || '$ 0.00' },
        { label: 'Total Sell Return:', value: summary?.total_sell_return?.formatted || '$ 0.00', hi: true },
        { label: 'Total Payroll:', value: summary?.left_side_module_data?.total_payroll || '$ 0.00' },
        { label: 'Total Production Cost:', value: summary?.left_side_module_data?.total_production_cost || '$ 0.00', hi: true },
    ];

    const rightRows = [
        { label: 'Closing stock (By purchase price):', value: summary?.closing_stock?.by_purchase_price || '$ 0.00' },
        { label: 'Total Sales: (Exc. tax, Discount)', value: summary?.total_sell?.exc_tax_discount || '$ 0.00', hi: true },
        { label: 'Total sell shipping charge:', value: summary?.total_sell_shipping_charge?.formatted || '$ 0.00' },
        { label: 'Sell additional expenses:', value: summary?.total_sell_additional_expense?.formatted || '$ 0.00', hi: true },
        { label: 'Total Stock Recovered:', value: summary?.total_recovered?.formatted || '$ 0.00' },
        { label: 'Total Purchase Return:', value: summary?.total_purchase_return?.formatted || '$ 0.00', hi: true },
        { label: 'Total Purchase discount:', value: summary?.total_purchase_discount?.formatted || '$ 0.00' },
        { label: 'Total sell round off:', value: summary?.total_sell_round_off?.formatted || '$ 0.00', hi: true },
    ];

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* ── Date Filter Bar ── */}
            <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative' }}>
                    <button onClick={() => setShowDateFilter(v => !v)} style={{ ...inputStyle, cursor: 'pointer', background: '#fff', color: '#334155', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <i className="fa fa-calendar" /> Filter by date ▾
                    </button>
                    {showDateFilter && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.25rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 20, minWidth: '180px' }}>
                            {datePresets.map((p, i) => (
                                <button key={i} onClick={() => applyDatePreset(p)} style={{ display: 'block', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: '#475569' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
                <input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} style={inputStyle} />

                <select value={filters.location_id} onChange={e => setFilters(f => ({ ...f, location_id: e.target.value }))} style={{ ...inputStyle, background: '#fff', cursor: 'pointer' }}>
                    <option value="">All Locations</option>
                    {Array.isArray(filterOptions.locations)
                        ? filterOptions.locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)
                        : Object.entries(filterOptions.locations || {}).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>

                {filters.start_date && filters.end_date && (
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {filters.start_date} to {filters.end_date}
                    </span>
                )}
            </div>

            {/* ── Two-column Metrics Grid ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    {leftRows.map((row, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.35rem 0.5rem', background: row.hi ? '#f8fafc' : 'transparent', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: '0.8125rem', color: '#334155', fontWeight: '500' }}>{row.label}</span>
                            <span style={{ fontSize: '0.8125rem', color: '#1e293b', fontWeight: '600' }}>{row.value}</span>
                        </div>
                    ))}
                </div>

                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    {rightRows.map((row, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.35rem 0.5rem', background: row.hi ? '#f8fafc' : 'transparent', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: '0.8125rem', color: '#334155', fontWeight: '500' }}>{row.label}</span>
                            <span style={{ fontSize: '0.8125rem', color: '#1e293b', fontWeight: '600' }}>{row.value}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Profit Summary Card ── */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '1rem', color: '#64748b' }}>
                        Gross Profit: <strong style={{ color: '#1e293b' }}>{summary?.gross_profit?.formatted || '$ 0.00'}</strong>
                    </span>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.2rem' }}>
                        (Total sell price - Total purchase price) + Project Invoice
                    </div>
                </div>
                <div>
                    <span style={{ fontSize: '1rem', color: '#64748b' }}>
                        Net Profit: <strong style={{ color: '#1e293b' }}>{summary?.net_profit?.formatted || '$ 0.00'}</strong>
                    </span>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.2rem', lineHeight: 1.5 }}>
                        Gross Profit + (Total sell shipping charge + Sell additional expenses + Total Stock Recovered + Total Purchase discount + Total sell round off)<br />
                        - (Total Stock Adjustment + Total Expense + Total purchase shipping charge + Total transfer shipping charge + Purchase additional expenses + Total Sell discount + Total customer reward + Total Payroll + Total Production Cost)
                    </div>
                </div>
            </div>

            {/* ── Sub-tabs ── */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e2e8f0', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {subTabs.map(tab => (
                    <button key={tab.id} onClick={() => setActiveSubTab(tab.id)} style={{ padding: '0.75rem 1rem', background: 'none', border: 'none', borderBottom: activeSubTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent', color: activeSubTab === tab.id ? '#3b82f6' : '#64748b', fontWeight: activeSubTab === tab.id ? '600' : '500', fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className={`fa ${tab.icon}`} /> {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Table Card ── */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                {activeSubTab === 'stock-by-sell-price' ? (
                    <div style={{ padding: '1.5rem' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Loading...</div>
                        ) : error ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</div>
                        ) : stockData ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.25rem', textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Opening Stock (By Selling Price)</div>
                                    <div style={{ fontSize: '1.5rem', color: '#1e293b', fontWeight: '700' }}>{stockData.opening_stock_by_sp?.formatted || '$ 0.00'}</div>
                                </div>
                                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.25rem', textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Closing Stock (By Selling Price)</div>
                                    <div style={{ fontSize: '1.5rem', color: '#1e293b', fontWeight: '700' }}>{stockData.closing_stock_by_sp?.formatted || '$ 0.00'}</div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No data available</div>
                        )}
                    </div>
                ) : (<>
                {/* Table controls */}
                <div className="table-controls" style={{ borderBottom: '1px solid #e2e8f0', padding: '0.75rem 1.25rem' }}>
                    <div className="table-controls-left">
                        <div className="entries-select">
                            <span>Show</span>
                            <select value={entriesPerPage} onChange={e => setEntriesPerPage(Number(e.target.value))}>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>

                    <div className="table-controls-right">
                        <div className="export-dropdown-container" style={{ position: 'relative' }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', color: '#334155' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '160px' }}>
                                    {exportItems.map((item, i) => (
                                        <button key={i} onClick={() => { if (item.action) item.action(); setShowExportDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: '#475569' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            <i className={`fa ${item.icon}`} style={{ width: '14px', textAlign: 'center' }} /> {item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '0.4rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none', width: '180px' }} />
                    </div>
                </div>

                {/* Table */}
                <div>
                    <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', tableLayout: 'fixed', minWidth: 'auto' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ width: '60%', padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b', fontWeight: '600' }}>{firstColLabel}</th>
                                <th style={{ width: '40%', padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b', fontWeight: '600', textAlign: 'right' }}>Gross Profit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="2" style={{ textAlign: 'center', padding: '2.5rem', color: '#94a3b8' }}>Loading...</td></tr>
                            ) : error ? (
                                <tr><td colSpan="2" style={{ textAlign: 'center', padding: '2.5rem', color: '#ef4444' }}>{error}</td></tr>
                            ) : paginatedData.length === 0 ? (
                                <tr><td colSpan="2" style={{ textAlign: 'center', padding: '2.5rem', color: '#94a3b8', fontSize: '0.9rem' }}>No data available in table</td></tr>
                            ) : paginatedData.map((row, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '0' }}>
                                        {row.product || row.category || row.brand || row.location || row.invoice_no || row.transaction_date_formatted || row.transaction_date || row.customer || row.day || '-'}
                                    </td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b', fontWeight: '600', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {row.gross_profit?.formatted || '$ 0.00'}
                                    </td>
                                </tr>
                            ))}
                            {paginatedData.length > 0 && (
                                <tr style={{ background: '#f8fafc', fontWeight: '600' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b' }}>Total</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b', textAlign: 'right' }}>{totalProfit.formatted}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="pagination" style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #e2e8f0' }}>
                    <div className="pagination-info" style={{ fontSize: '0.8125rem' }}>
                        Showing {filteredData.length > 0 ? 1 : 0} to {Math.min(entriesPerPage, filteredData.length)} of {filteredData.length} entries
                    </div>
                </div>
            </>)}
            </div>
        </div>
    );
};

export default ProfitLossReport;
